/**
 * Contenido comercial de SISTEMA IA PARA NUTRICIONISTAS.
 *
 * Regla editorial: no se inventan testimonios, compradores, certificaciones,
 * cantidades de ventas, escasez ni pruebas sociales que no hayan sido
 * provistas. Los números corresponden a lo que el producto contiene.
 */

export interface Metric {
  value: string
  label: string
}

export interface Kit {
  id: string
  number: string
  category: string
  name: string
  benefit: string
  capabilities: string[]
}

export interface Bono {
  number: string
  title: string
  subtitle: string
  body: string
}

export interface InsidePiece {
  image: string
  alt: string
  /** Posición dentro de la composición editorial (mobile → desktop). */
  className: string
}

export interface Faq {
  question: string
  answer: string
}

export const PRODUCT_NAME = 'Sistema IA para Nutricionistas'

export const PRICE = {
  current: '$19.990',
  currency: 'ARS',
  note: 'Pago único · Acceso inmediato',
}

/** Ancla de la primera revelación de precio. El CTA del hero scrollea acá. */
export const OFFER_ANCHOR = '#oferta'

export const TICKER_ITEMS: string[] = [
  '8 KITS ESPECIALIZADOS',
  'RECUPERÁ 20 HORAS POR SEMANA',
  'ASISTENTE',
  'CONSULTA',
  'PLANES',
  'ADHERENCIA',
  'EDUCACIÓN',
  'GESTIÓN',
  'COMANDOS',
  'DATOS',
  '5 BONOS INCLUIDOS',
  'ACCESO INMEDIATO',
  'PAGÁ CON MERCADO PAGO',
]

export const HERO_METRICS: Metric[] = [
  { value: '8', label: 'Kits especializados' },
  { value: '60+', label: 'Comandos profesionales' },
  { value: '20 h', label: 'Recuperadas por semana' },
  { value: '5', label: 'Bonos incluidos' },
]

export const PROBLEM_TASKS: Array<{ id: string; label: string }> = [
  { id: 'preparar', label: 'Preparar sesiones' },
  { id: 'informacion', label: 'Ordenar información' },
  { id: 'planes', label: 'Adaptar planes' },
  { id: 'seguimiento', label: 'Hacer seguimiento' },
  { id: 'pacientes', label: 'Responder pacientes' },
  { id: 'materiales', label: 'Crear materiales' },
  { id: 'gestion', label: 'Gestionar el consultorio' },
  { id: 'datos', label: 'Revisar datos' },
]

export const PROBLEM_CLOSER =
  'Son horas de trabajo profesional gastadas en tareas que hoy podés acelerar con IA.'

export const SYSTEM_CATEGORIES: string[] = [
  'Asistente',
  'Consulta',
  'Planes',
  'Adherencia',
  'Educación',
  'Gestión',
  'Comandos',
  'Datos',
]

export const KITS: Kit[] = [
  {
    id: 'asistente',
    number: '01',
    category: 'Asistente',
    name: 'El cerebro digital',
    benefit:
      'Organizá información, contexto y procesos para construir tu asistente profesional con IA.',
    capabilities: ['Organización', 'Contexto', 'Asistente personalizado', 'Trabajo repetible'],
  },
  {
    id: 'consulta',
    number: '02',
    category: 'Consulta',
    name: 'Consulta de alta precisión',
    benefit: 'Usá IA para preparar, organizar y procesar mejor cada consulta.',
    capabilities: ['Pre-consulta', 'Notas', 'Información', 'Seguimiento'],
  },
  {
    id: 'planes',
    number: '03',
    category: 'Planes',
    name: 'Diseño estratégico de planes',
    benefit:
      'Agilizá la estructuración y adaptación de planes bajo tus propios criterios profesionales.',
    capabilities: ['Estructuración', 'Adaptaciones', 'Alternativas', 'Organización'],
  },
  {
    id: 'adherencia',
    number: '04',
    category: 'Adherencia',
    name: 'El paciente adherente',
    benefit: 'Utilizá IA para fortalecer seguimiento, comunicación y continuidad.',
    capabilities: ['Barreras', 'Seguimiento', 'Mensajes', 'Adherencia'],
  },
  {
    id: 'educacion',
    number: '05',
    category: 'Educación',
    name: 'Educación nutricional inteligente',
    benefit: 'Transformá conceptos profesionales en materiales claros y fáciles de comprender.',
    capabilities: ['Explicaciones', 'Materiales', 'FAQ', 'Educación'],
  },
  {
    id: 'gestion',
    number: '06',
    category: 'Gestión',
    name: 'Consultorio inteligente',
    benefit: 'Reducí trabajo operativo y organizá tareas recurrentes del consultorio.',
    capabilities: ['Administración', 'Comunicación', 'Procesos', 'Organización'],
  },
  {
    id: 'comandos',
    number: '07',
    category: 'Comandos',
    name: 'Biblioteca de comandos',
    benefit: '60 comandos profesionales listos para adaptar y utilizar.',
    capabilities: ['60 comandos', 'Copiar', 'Personalizar', 'Implementar'],
  },
  {
    id: 'datos',
    number: '08',
    category: 'Datos',
    name: 'Maestría en análisis de datos',
    benefit: 'Utilizá IA para organizar, comparar y detectar patrones en información profesional.',
    capabilities: ['Organización', 'Comparación', 'Patrones', 'Decisiones'],
  },
]

/**
 * Composición editorial de la sección "Mirá todo lo que tenés en tus manos".
 * Cada pieza es una preview real de material interior del sistema.
 */
export const INSIDE_COMPOSITION: InsidePiece[] = [
  {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b01d5cb7-d70a-4010-a152-a0572dad39cc.jpg',
    alt: 'Tomo abierto mostrando un sistema de trabajo y una tabla de módulos.',
    className:
      'left-[2%] top-[20%] z-20 w-[74%] -rotate-2 sm:left-[15%] sm:top-[15%] sm:w-[44%] sm:-rotate-1',
  },
  {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/87ad66c7-af61-4269-ae2e-d3b4751f7d51.jpg',
    alt: 'Páginas sueltas del sistema: checklists, tablas y comandos.',
    className:
      'right-0 top-0 z-10 w-[46%] rotate-3 sm:right-[5%] sm:top-[4%] sm:w-[31%] sm:rotate-2',
  },
  {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e059a1df-3be2-4379-9fc7-d617244f05da.jpg',
    alt: 'Página interior de la biblioteca de comandos profesionales.',
    className:
      'bottom-[4%] left-0 z-10 w-[42%] rotate-3 sm:bottom-[6%] sm:left-[1%] sm:w-[26%] sm:rotate-2',
  },
  {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/b675de10-b0e2-40d7-a103-785cebd7d066.jpg',
    alt: 'Página interior con una guía paso a paso.',
    className:
      'hidden sm:block sm:left-[0%] sm:top-[2%] sm:z-0 sm:w-[22%] sm:-rotate-3',
  },
  {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d02750b8-53aa-48a0-8b49-d3a23073bec2.jpg',
    alt: 'Plantilla editable con tabla de datos.',
    className:
      'hidden sm:block sm:bottom-[0%] sm:right-[2%] sm:z-0 sm:w-[24%] sm:rotate-[4deg]',
  },
  {
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d8147d37-11a2-4302-a3a3-7a6235eb5298.jpg',
    alt: 'Tablet mostrando un checklist del sistema en pantalla.',
    className:
      'bottom-0 right-[3%] z-30 w-[32%] -rotate-3 sm:bottom-[1%] sm:right-[24%] sm:w-[18%] sm:-rotate-2',
  },
]

export const INSIDE_LEAD =
  'Guías, plantillas, tablas, checklists y comandos: material concreto para abrir y usar, no teoría sobre Inteligencia Artificial.'

export const INSIDE_CLOSER =
  'Material listo para aplicar, adaptar y llevar a tu trabajo diario.'

export const INSIDE_TAGS: string[] = [
  'Guías paso a paso',
  'Plantillas',
  'Comandos',
  'Tablas',
  'Sistemas',
  'Checklists',
  'Ejemplos',
  'Implementación',
]

export const BONOS_IMAGE =
  'https://cdn.magicpatterns.com/patterns/generated-images/b9dc9b54-036b-47b7-bad2-1a5a42a8d500.jpg'

export const BONOS: Bono[] = [
  {
    number: '01',
    title: 'Implementación express',
    subtitle: 'Tu consultorio IA en 7 días',
    body: 'Pasá de tener el sistema guardado a empezar a implementarlo en tu consultorio con un plan claro de 7 días.',
  },
  {
    number: '02',
    title: 'Pack de plantillas editables',
    subtitle: 'Consultorio IA Ready',
    body: 'Dejá de crear documentos desde cero: completá, adaptá y reutilizá plantillas listas para tu trabajo diario.',
  },
  {
    number: '03',
    title: 'Auditor IA profesional',
    subtitle: 'Checklists de revisión segura',
    body: 'Revisá cada resultado de la IA con puntos de control claros antes de llevarlo a tu trabajo profesional.',
  },
  {
    number: '04',
    title: 'Swipe file PRO',
    subtitle: '75 scripts listos para consultorio nutricional',
    body: '75 mensajes y respuestas listas para adaptar a las conversaciones que se repiten todas las semanas.',
  },
  {
    number: '05',
    title: 'Dashboard de implementación',
    subtitle: 'Seguimiento del ahorro de tiempo con IA',
    body: 'Visualizá qué ya implementaste, cuánto trabajo estás recuperando y cuál es el próximo proceso que podés mejorar.',
  },
]

/**
 * Resumen compacto de la caja de oferta. Reemplaza la enumeración kit por kit:
 * el visitante ya recorrió cada kit y cada bono más arriba en la carta.
 */
export const OFFER_SUMMARY: string[] = [
  '8 kits profesionales de IA',
  '60+ comandos profesionales',
  '5 bonos de implementación',
  'Plantillas, sistemas y checklists',
  'Acceso inmediato',
]

export const FAQS: Faq[] = [
  {
    question: '¿Necesito saber usar Inteligencia Artificial?',
    answer:
      'No. Cada kit te muestra qué información preparar, qué comando utilizar y qué revisar. Podés empezar aunque hoy uses IA solamente para consultas básicas.',
  },
  {
    question: '¿Necesito ChatGPT Plus?',
    answer:
      'No. Podés aplicar el sistema utilizando versiones gratuitas de herramientas de IA compatibles con los procesos explicados. No necesitás contratar ChatGPT Plus para comenzar.',
  },
  {
    question: '¿Esto reemplaza mi criterio profesional?',
    answer:
      'No. Todo lo contrario. La IA funciona como asistente para organizar, procesar, redactar y acelerar tareas. Las decisiones profesionales, la revisión y la indicación al paciente siguen estando siempre a cargo del nutricionista.',
  },
  {
    question: '¿Sirve para cualquier tipo de nutricionista?',
    answer:
      'Sí. Los sistemas y comandos están diseñados para adaptarse a diferentes especialidades, metodologías de trabajo y tipos de pacientes. Vos incorporás tus propios criterios profesionales.',
  },
  {
    question: '¿Cómo recibo el material?',
    answer:
      'Después de completar la compra obtenés acceso digital al Sistema IA para Nutricionistas, sus 8 kits y los bonos incluidos.',
  },
  {
    question: '¿El acceso es inmediato?',
    answer:
      'Sí. Una vez confirmada la compra podés acceder al material digital y empezar a implementarlo.',
  },
  {
    question: '¿Puedo usar los comandos con mis propios pacientes?',
    answer:
      'Sí. Los comandos están pensados para ser personalizados con tu contexto y metodología profesional. Siempre protegé los datos sensibles y revisá cada resultado antes de utilizarlo.',
  },
  {
    question: '¿Qué incluye exactamente mi compra?',
    answer:
      'Recibís los 8 kits del Sistema IA para Nutricionistas, 60+ comandos profesionales y los 5 bonos de implementación mostrados en esta página.',
  },
  {
    question: '¿Cómo funciona la garantía?',
    answer:
      'Tenés 7 días para acceder y probar el sistema. Si dentro de ese período considerás que no es para vos, podés solicitar la devolución según las condiciones comerciales aplicables.',
  },
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'El sistema', href: '#sistema' },
  { label: 'Los 8 kits', href: '#kits' },
  { label: 'Por dentro', href: '#por-dentro' },
  { label: 'Bonos', href: '#bonos' },
  { label: 'Preguntas', href: '#faq' },
]

/** Contactos reales del footer. Sin placeholders ni links que vuelvan al inicio. */
export const BRAND = {
  studio: 'Creativo Digital',
  email: 'atencion.creativodigital@gmail.com',
  instagramHandle: '@creativodigitalstudio',
  instagramUrl: 'https://www.instagram.com/creativodigitalstudio/',
  siteLabel: 'creativodigital.shop',
  siteUrl: 'https://creativodigital.shop/',
}
